import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wrench, Zap, CheckCircle, Clock, Play, AlertTriangle, Shield } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutoFixPage() {
  const { getAuthHeaders } = useAuth();
  const [fixes, setFixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchFixes = async () => {
    try {
      const response = await axios.get(`${API_URL}/ai/auto-fixes`, {
        headers: getAuthHeaders()
      });
      setFixes(response.data);
    } catch (error) {
      console.error('Failed to fetch fixes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFixes();
  }, [getAuthHeaders]);

  const handleGenerateFix = async (errorId, fixType) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/ai/auto-fix`, {
        error_id: errorId,
        fix_type: fixType,
        parameters: {}
      }, {
        headers: getAuthHeaders()
      });
      
      setFixes([response.data, ...fixes]);
      toast.success('Fix plan generated!');
    } catch (error) {
      toast.error('Failed to generate fix');
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyFix = async (fixId) => {
    try {
      await axios.post(`${API_URL}/ai/auto-fix/${fixId}/apply`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Fix applied successfully!');
      fetchFixes();
    } catch (error) {
      toast.error('Failed to apply fix');
    }
  };

  const getSafetyColor = (level) => {
    switch (level) {
      case 'safe': return 'text-accent bg-accent/20 border-accent/30';
      case 'requires-review': return 'text-yellow-500 bg-yellow-500/20 border-yellow-500/30';
      case 'risky': return 'text-destructive bg-destructive/20 border-destructive/30';
      default: return 'text-primary bg-primary/20 border-primary/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'applied': return <CheckCircle className="w-5 h-5 text-accent" />;
      case 'applying': return <Clock className="w-5 h-5 text-yellow-500 animate-spin" />;
      default: return <Wrench className="w-5 h-5 text-primary" />;
    }
  };

  // Demo error for generating fix
  const demoError = {
    id: 'demo-error-1',
    type: 'connection_pool_exhaustion'
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3" data-testid="autofix-title">
            <Wrench className="w-8 h-8 text-accent" />
            AI Auto-Fix
          </h1>
          <p className="text-foreground/60 mt-1">
            Intelligent automated fixes for detected errors
          </p>
        </motion.div>

        {/* Generate Fix Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-secondary" />
                Generate New Fix
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Click below to generate an AI-powered fix for common issues
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => handleGenerateFix(demoError.id, 'connection_pool')}
                  disabled={generating}
                  className="bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20"
                  data-testid="generate-connection-fix"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Connection Pool Fix
                </Button>
                <Button
                  onClick={() => handleGenerateFix(demoError.id, 'memory_optimization')}
                  disabled={generating}
                  className="bg-secondary/10 border border-secondary/30 text-secondary hover:bg-secondary/20"
                  data-testid="generate-memory-fix"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Memory Optimization
                </Button>
                <Button
                  onClick={() => handleGenerateFix(demoError.id, 'performance_tuning')}
                  disabled={generating}
                  className="bg-accent/10 border border-accent/30 text-accent hover:bg-accent/20"
                  data-testid="generate-performance-fix"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Performance Tuning
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Fixes List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : fixes.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-16 text-center">
              <Wrench className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-heading font-semibold text-white mb-2">No Fix Plans Yet</h3>
              <p className="text-muted-foreground">
                Generate a fix from the Error Detective or use the buttons above
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="font-heading text-xl font-semibold text-white">Fix Plans</h2>
            {fixes.map((fix, i) => (
              <motion.div
                key={fix.id || i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-card/50 border-border">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-start gap-3">
                        {getStatusIcon(fix.status)}
                        <div>
                          <h3 className="font-heading font-semibold text-white">
                            {fix.fix_plan?.description || 'Fix Plan'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Created: {new Date(fix.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {fix.fix_plan?.safety_level && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getSafetyColor(fix.fix_plan.safety_level)}`}>
                            <Shield className="w-3 h-3 inline mr-1" />
                            {fix.fix_plan.safety_level}
                          </span>
                        )}
                        <span className={`px-3 py-1 rounded text-xs font-medium capitalize ${
                          fix.status === 'applied' ? 'bg-accent/20 text-accent' :
                          fix.status === 'applying' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {fix.status}
                        </span>
                      </div>
                    </div>

                    {/* Fix Steps */}
                    {fix.fix_plan?.steps && (
                      <div className="space-y-3 mb-4">
                        <h4 className="text-sm font-medium text-foreground/80">Fix Steps:</h4>
                        {fix.fix_plan.steps.map((step, j) => (
                          <div key={j} className="p-3 bg-[#0A0E27] rounded-lg border border-border">
                            <div className="flex items-start gap-3">
                              <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs text-primary font-bold">
                                {step.step || j + 1}
                              </span>
                              <div className="flex-1">
                                <p className="text-sm text-white">{step.action}</p>
                                {step.command && (
                                  <code className="block mt-2 p-2 bg-black rounded text-xs text-accent font-code">
                                    {step.command}
                                  </code>
                                )}
                                {step.expected_outcome && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Expected: {step.expected_outcome}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Success Indicators */}
                    {fix.fix_plan?.success_indicators && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-foreground/80 mb-2">Success Indicators:</h4>
                        <ul className="space-y-1">
                          {fix.fix_plan.success_indicators.map((indicator, j) => (
                            <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-accent" />
                              {indicator}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Side Effects Warning */}
                    {fix.fix_plan?.potential_side_effects && fix.fix_plan.potential_side_effects.length > 0 && (
                      <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-yellow-500">Potential Side Effects</h4>
                            <ul className="mt-1 space-y-1">
                              {fix.fix_plan.potential_side_effects.map((effect, j) => (
                                <li key={j} className="text-xs text-yellow-500/80">{effect}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Apply Button */}
                    {fix.status === 'pending' && (
                      <Button
                        onClick={() => handleApplyFix(fix.id)}
                        className="bg-accent text-black font-bold hover:shadow-neon-green"
                        data-testid={`apply-fix-${fix.id}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Apply Fix
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
